import { Component, OnDestroy, OnInit } from "@angular/core";
import { Subject, Subscription, catchError, of, switchMap, tap } from "rxjs";
import {
  isExtraProductInformationAvailable,
  isExtraProductInformationEnabled,
  WhiteLabelSetupConfiguration
} from "src/app/data-models/wl-account-setup/wl-config-data.model";
import {
  ExtraProductInformationVisibility,
  WhiteLabelConfigurationService
} from "src/app/services/white-labels/white-label-configuration.service";
import { ToastService, ToastType } from "src/app/services/toast.service";

@Component({
  selector: "extra-information-control",
  templateUrl: "./extra-information-control.component.html",
  styleUrls: ["./extra-information-control.component.scss"]
})
export class ExtraInformationControlComponent implements OnInit, OnDestroy {

  static readonly saveSuccessMessage = "Changes saved!";
  static readonly saveErrorMessage = "Changes unsaved!";

  public isAvailable = false;
  public extraInformationCheckbox = false;
  private whiteLabelSlug: string;

  private readonly requestedState: Subject<boolean> = new Subject();
  private subscriptions: Subscription = new Subscription();
  private savedState = false;

  constructor(
    private wlConfigService: WhiteLabelConfigurationService,
    private toastService: ToastService
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(
      this.wlConfigService.whiteLabelSetupConfiguration.subscribe((configuration: WhiteLabelSetupConfiguration) => {
        this.whiteLabelSlug = configuration.slug;
        this.isAvailable = isExtraProductInformationAvailable(configuration);
        this.savedState = isExtraProductInformationEnabled(configuration);
        this.extraInformationCheckbox = this.savedState;
      })
    );

    this.subscriptions.add(this.saveRequests().subscribe());
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public updateExtraInformationVisibility(isEnabled: boolean): void {
    // Follow the checkbox straight away, so a failed save can visibly move it back.
    this.extraInformationCheckbox = isEnabled;
    this.requestedState.next(isEnabled);
  }

  /**
   * One stream for every click: switchMap keeps only the answer to the latest click, so the checkbox and the
   * toast follow the last choice. A request already sent is not withdrawn from the server.
   */
  private saveRequests() {
    return this.requestedState.pipe(
      switchMap((isEnabled: boolean) => {
        const payload: ExtraProductInformationVisibility = { "extra_product_information_enabled": isEnabled };

        return this.wlConfigService.updateExtraProductInformationVisibility(this.whiteLabelSlug, payload).pipe(
          tap(() => {
            this.savedState = isEnabled;
            this.toastService.showToast(ExtraInformationControlComponent.saveSuccessMessage, ToastType.success);
          }),
          catchError(() => {
            this.restoreCheckbox();
            this.toastService.showToast(ExtraInformationControlComponent.saveErrorMessage, ToastType.error);
            return of(null);
          })
        );
      })
    );
  }

  /**
   * The user already moved the native checkbox, so the value has to travel back through a change detection
   * pass to be written to the DOM. Restoring it inside the same pass (a failure that arrives synchronously)
   * would leave the property and the checkbox out of step.
   */
  private restoreCheckbox(): void {
    setTimeout(() => this.extraInformationCheckbox = this.savedState);
  }
}
